/**
 * Chat Sync Context
 *
 * Provides synchronized chat state across browser windows.
 * Used for the pop-out chat feature.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sendChatMessageStream, type ChatPageContext } from '../api/client';
import { useChatSync, type ChatMessage } from '../hooks/useChatSync';
import type { ChatResponse, ChatAction, ChatCard } from '../types';

const TASK_MUTATION_TYPES = new Set([
  'create_task',
  'update_task',
  'complete_task',
  'decompose_task',
  'refetch_tasks',
]);

const CHAT_HISTORY_KEY = 'danny_chat_history';
const MAX_PERSISTED_MESSAGES = 100;

function loadPersistedMessages(): ChatMessage[] {
  try {
    const stored = localStorage.getItem(CHAT_HISTORY_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as ChatMessage[];
    return parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return [];
  }
}

function persistMessages(messages: ChatMessage[]): void {
  try {
    const toStore = messages.slice(-MAX_PERSISTED_MESSAGES);
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(toStore));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Dispatch navigation custom events from AI actions.
 * Also broadcasts to other windows via BroadcastChannel for pop-out support.
 */
type NavigationMessage =
  | { type: 'NAVIGATE_VIEW'; payload: { viewSlug: string } }
  | { type: 'SELECT_TASK'; payload: { taskId: string } };

function dispatchNavigationEvents(
  actions: ChatAction[],
  broadcastNavigation: (msg: NavigationMessage) => void,
): void {
  for (const action of actions) {
    if (action.type === 'navigate_view' && action.viewSlug) {
      window.dispatchEvent(
        new CustomEvent('danny-navigate-view', { detail: { viewSlug: action.viewSlug } }),
      );
      broadcastNavigation({ type: 'NAVIGATE_VIEW', payload: { viewSlug: action.viewSlug } });
    }
    if (action.type === 'select_task' && action.taskId) {
      window.dispatchEvent(
        new CustomEvent('danny-select-task', { detail: { taskId: action.taskId } }),
      );
      broadcastNavigation({ type: 'SELECT_TASK', payload: { taskId: action.taskId } });
    }
    if (action.type === 'add_comment' && action.taskId) {
      // Danny attached a comment to a task — refresh an open comments panel for that task.
      window.dispatchEvent(
        new CustomEvent('danny-comments-updated', { detail: { taskId: action.taskId } }),
      );
    }
  }
}

interface ChatSyncContextValue {
  messages: ChatMessage[];
  isLoading: boolean;
  streamingContent: string;
  toolStatus: string | null;
  sendMessageStream: (
    content: string,
    context?: ChatPageContext
  ) => Promise<ChatResponse | null>;
  clearMessages: () => void;
  // Pop-out management
  isPopOutOpen: boolean;
  openPopOut: () => void;
  closePopOut: () => void;
  // Context for pop-out window
  chatContext: ChatPageContext | undefined;
  setChatContext: (context: ChatPageContext | undefined) => void;
}

const ChatSyncContext = createContext<ChatSyncContextValue | null>(null);

interface ChatSyncProviderProps {
  children: ReactNode;
  isPopOut?: boolean;
}

export function ChatSyncProvider({ children, isPopOut = false }: ChatSyncProviderProps) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>(loadPersistedMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [isPopOutOpen, setIsPopOutOpen] = useState(false);
  const [chatContext, setChatContext] = useState<ChatPageContext | undefined>();

  const popOutWindowRef = useRef<Window | null>(null);
  const streamingIdRef = useRef<string | null>(null);

  // Persist messages to localStorage — only from the primary window to avoid
  // the pop-out overwriting with stale data during streaming
  useEffect(() => {
    if (!isPopOut) {
      persistMessages(messages);
    }
  }, [messages, isPopOut]);

  // Refs for current state — used in sync callbacks to avoid stale closures
  // and to keep syncOptions stable (prevents BroadcastChannel teardown/recreate)
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const chatContextRef = useRef(chatContext);
  chatContextRef.current = chatContext;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const streamingContentRef = useRef(streamingContent);
  streamingContentRef.current = streamingContent;
  const toolStatusRef = useRef(toolStatus);
  toolStatusRef.current = toolStatus;

  // Refs for broadcast functions — populated after useChatSync returns.
  // Allows syncOptions to be stable (no deps) while still calling broadcast fns.
  const broadcastFnsRef = useRef<{
    respondSync: (msgs: ChatMessage[]) => void;
    broadcastContext: (ctx: unknown) => void;
    broadcastLoading: (loading: boolean) => void;
    broadcastStreaming: (content: string, toolStatus: string | null) => void;
  } | null>(null);

  // Set up cross-window sync — syncOptions is stable (no state deps)
  // so the BroadcastChannel is created once and never torn down
  const syncOptions = useMemo(
    () => ({
      onMessagesUpdate: (newMessages: ChatMessage[]) => {
        setMessages(newMessages);
      },
      onStreamingUpdate: (content: string, status: string | null) => {
        setStreamingContent(content);
        setToolStatus(status);
      },
      onLoadingUpdate: (loading: boolean) => {
        setIsLoading(loading);
      },
      onClear: () => {
        setMessages([]);
        setStreamingContent('');
        setToolStatus(null);
        streamingIdRef.current = null;
        localStorage.removeItem(CHAT_HISTORY_KEY);
      },
      onSyncRequest: () => {
        // Another window is requesting current state — send everything
        const fns = broadcastFnsRef.current;
        if (!fns) return;
        fns.respondSync(messagesRef.current);
        fns.broadcastContext(chatContextRef.current);
        fns.broadcastLoading(isLoadingRef.current);
        fns.broadcastStreaming(streamingContentRef.current, toolStatusRef.current);
      },
      onContextUpdate: (context: unknown) => {
        // Receive context updates from main window
        setChatContext(context as ChatPageContext | undefined);
      },
    }),
     
    []
  );

  const {
    broadcastMessages,
    broadcastStreaming,
    broadcastLoading,
    broadcastClear,
    requestSync,
    respondSync,
    broadcastContext,
    broadcastNavigation,
  } = useChatSync(syncOptions);

  // Keep broadcast refs fresh
  broadcastFnsRef.current = { respondSync, broadcastContext, broadcastLoading, broadcastStreaming };

  // Wrapper that broadcasts context changes to other windows
  const setChatContextWithBroadcast = useCallback(
    (context: ChatPageContext | undefined) => {
      setChatContext(context);
      broadcastContext(context);
    },
    [broadcastContext]
  );

  // Request sync from other windows when pop-out opens
  const handlePopOutReady = useCallback(() => {
    if (isPopOut) {
      requestSync();
    }
  }, [isPopOut, requestSync]);

  // Request sync on mount for pop-out
  useEffect(() => {
    if (isPopOut) {
      // Small delay to ensure channel is set up
      const timer = setTimeout(handlePopOutReady, 100);
      return () => clearTimeout(timer);
    }
  }, [isPopOut, handlePopOutReady]);

  const sendMessageStreamHandler = useCallback(
    async (
      content: string,
      context?: ChatPageContext
    ): Promise<ChatResponse | null> => {
      if (!content.trim()) return null;

      // Add user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      // Build history from recent messages (last 10 exchanges for context)
      const history = messages.slice(-10).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const newMessages = [...messages, userMessage];
      setMessages(newMessages);
      broadcastMessages(newMessages);

      setIsLoading(true);
      broadcastLoading(true);

      setStreamingContent('');
      setToolStatus(null);
      broadcastStreaming('', null);

      // Create placeholder for streaming assistant message
      const assistantId = `assistant-${Date.now()}`;
      streamingIdRef.current = assistantId;

      let finalContent = '';
      let finalActions: ChatAction[] | undefined;
      let finalCards: ChatCard[] | undefined;

      try {
        await sendChatMessageStream(
          content,
          {
            onStatus: (message) => {
              setToolStatus(message);
              broadcastStreaming(finalContent, message);
            },
            onTool: (_name, description) => {
              setToolStatus(description);
              broadcastStreaming(finalContent, description);
            },
            onToken: (text) => {
              finalContent += text;
              setStreamingContent(finalContent);
              setToolStatus(null);
              broadcastStreaming(finalContent, null);
            },
            onActions: (actions) => {
              finalActions = actions;
              dispatchNavigationEvents(actions, broadcastNavigation);
              if (actions.some((a) => TASK_MUTATION_TYPES.has(a.type))) {
                queryClient.invalidateQueries({ queryKey: ['tasks'] });
              }
            },
            onCards: (cards) => {
              finalCards = cards;
            },
            onDone: () => {
              // Finalize the message
              const assistantMessage: ChatMessage = {
                id: assistantId,
                role: 'assistant',
                content: finalContent,
                timestamp: new Date(),
                actions: finalActions,
                cards: finalCards,
              };

              const updatedMessages = [...newMessages, assistantMessage];
              setMessages(updatedMessages);
              broadcastMessages(updatedMessages);

              setStreamingContent('');
              setToolStatus(null);
              broadcastStreaming('', null);

              streamingIdRef.current = null;
            },
            onError: (message) => {
              // Add error message
              const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Sorry, I ran into an issue: ${message}`,
                timestamp: new Date(),
              };

              const updatedMessages = [...newMessages, errorMessage];
              setMessages(updatedMessages);
              broadcastMessages(updatedMessages);

              setStreamingContent('');
              setToolStatus(null);
              broadcastStreaming('', null);

              streamingIdRef.current = null;
            },
          },
          history,
          context || chatContext
        );

        return {
          response: finalContent,
          success: true,
          actions: finalActions,
        };
      } catch (err) {
        console.error('Chat stream error:', err);
        return null;
      } finally {
        setIsLoading(false);
        broadcastLoading(false);
      }
    },
    [messages, chatContext, broadcastMessages, broadcastStreaming, broadcastLoading]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStreamingContent('');
    setToolStatus(null);
    streamingIdRef.current = null;
    localStorage.removeItem(CHAT_HISTORY_KEY);
    broadcastClear();
  }, [broadcastClear]);

  const openPopOut = useCallback(() => {
    if (popOutWindowRef.current && !popOutWindowRef.current.closed) {
      popOutWindowRef.current.focus();
      return;
    }

    const popOut = window.open(
      '/chat/popout',
      'danny-chat',
      'width=420,height=600,resizable=yes,scrollbars=yes'
    );

    if (popOut) {
      popOutWindowRef.current = popOut;
      setIsPopOutOpen(true);

      // Immediately broadcast current state so pop-out gets it
      // Uses refs to avoid stale closure values during streaming
      setTimeout(() => {
        broadcastMessages(messagesRef.current);
        broadcastStreaming(streamingContentRef.current, toolStatusRef.current);
        broadcastLoading(isLoadingRef.current);
        broadcastContext(chatContextRef.current);
      }, 200);
    }
  }, [broadcastMessages, broadcastStreaming, broadcastLoading, broadcastContext]);

  const closePopOut = useCallback(() => {
    if (popOutWindowRef.current && !popOutWindowRef.current.closed) {
      popOutWindowRef.current.close();
    }
    popOutWindowRef.current = null;
    setIsPopOutOpen(false);
  }, []);

  // Check if pop-out window is still open
  useEffect(() => {
    if (!isPopOutOpen) return;

    const checkInterval = setInterval(() => {
      if (popOutWindowRef.current?.closed) {
        popOutWindowRef.current = null;
        setIsPopOutOpen(false);
      }
    }, 500);

    return () => clearInterval(checkInterval);
  }, [isPopOutOpen]);

  const value = useMemo(
    (): ChatSyncContextValue => ({
      messages,
      isLoading,
      streamingContent,
      toolStatus,
      sendMessageStream: sendMessageStreamHandler,
      clearMessages,
      isPopOutOpen,
      openPopOut,
      closePopOut,
      chatContext,
      setChatContext: setChatContextWithBroadcast,
    }),
    [
      messages,
      isLoading,
      streamingContent,
      toolStatus,
      sendMessageStreamHandler,
      clearMessages,
      isPopOutOpen,
      openPopOut,
      closePopOut,
      chatContext,
      setChatContextWithBroadcast,
    ]
  );

  return (
    <ChatSyncContext.Provider value={value}>{children}</ChatSyncContext.Provider>
  );
}

export function useChatSyncContext() {
  const context = useContext(ChatSyncContext);
  if (!context) {
    throw new Error('useChatSyncContext must be used within a ChatSyncProvider');
  }
  return context;
}
